import { Capacitor } from '@capacitor/core';
import api from '../config/api';

// Dynamically import PushNotifications only on native platforms
let PushNotifications = null;

const getPushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  
  if (!PushNotifications) {
    try {
      const module = await import('@capacitor/push-notifications');
      PushNotifications = module.PushNotifications;
    } catch (error) {
      console.warn('PushNotifications plugin not available:', error);
      return null;
    }
  }
  
  return PushNotifications;
};

class PushNotificationService {
  constructor() {
    this.fcmToken = null;
    this.isRegistered = false;
    this.listeners = [];
  }

  async initialize() {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications only work on native platforms');
      return;
    }

    const PushNotifications = await getPushNotifications();
    if (!PushNotifications) {
      console.warn('PushNotifications plugin not available');
      return;
    }

    try {
      // Request permission
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive !== 'granted') {
        console.error('Push notification permission denied');
        return;
      }

      // Register for push notifications
      await PushNotifications.register();

      // Listen for registration
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success, token: ' + token.value);
        this.fcmToken = token.value;
        this.isRegistered = true;
        
        // Send token to your backend
        this.sendTokenToServer(token.value);
      });

      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Error on registration: ' + JSON.stringify(error));
      });

      // Listen for push notifications received while app is open
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received: ', notification);
        // Handle notification when app is in foreground
        this.handleNotification(notification);
      });

      // Listen for push notifications tapped/opened
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed', notification.actionId, notification.inputValue);
        // Handle notification tap - navigate to relevant screen
        this.handleNotificationTap(notification);
        // Broadcast to listeners so screens can refresh
        window.dispatchEvent(new CustomEvent('pushNotificationAction', {
          detail: {
            title: notification.notification?.title,
            body: notification.notification?.body,
            data: notification.notification?.data || notification.data || {}
          }
        }));
      });

      console.log('Push notifications initialized successfully');
    } catch (error) {
      // Silently handle errors on web platform
      if (Capacitor.getPlatform() === 'web') {
        console.log('Push notifications not available on web platform');
      } else {
        console.error('Error initializing push notifications:', error);
      }
    }
  }

  async sendTokenToServer(token) {
    try {
      const response = await api.post('/api/users/fcm-token', {
        fcmToken: token,
        platform: 'android'
      });
      console.log('FCM token saved to server:', response.data);
    } catch (error) {
      console.error('Error saving FCM token:', error);
      // Retry after a delay if user is not authenticated yet
      if (error.response?.status === 401) {
        setTimeout(() => {
          this.sendTokenToServer(token);
        }, 5000);
      }
    }
  }

  handleNotification(notification) {
    // Show in-app notification or update UI
    const data = notification.data || {};
    
    // Trigger custom event for other parts of the app to listen
    window.dispatchEvent(new CustomEvent('pushNotificationReceived', {
      detail: {
        title: notification.title || data.title,
        body: notification.body || data.body || data.message,
        data: data
      }
    }));

    // You can also use react-hot-toast if available
    if (window.toast && notification.title) {
      window.toast.success(notification.body || notification.title);
    }
  }

  handleNotificationTap(notification) {
    // Navigate to relevant screen based on notification data
    const data = notification.notification?.data || {};
    const type = data.type;

    window.dispatchEvent(new CustomEvent('pushNotificationAction', {
      detail: {
        title: notification.notification?.title || data.title,
        body: notification.notification?.body || data.body || data.message,
        data
      }
    }));

    // Use window.location for navigation
    if (type === 'booking_request' || type === 'booking_accepted' || type === 'booking_rejected') {
      window.location.href = '/my-bookings';
    } else if (type === 'trip_started' || type === 'trip_completed') {
      if (data.tripId) {
        window.location.href = `/trip/${data.tripId}`;
      } else {
        window.location.href = '/my-trips';
      }
    } else if (type === 'message') {
      window.location.href = '/messages';
    } else if (type === 'payout_requested' || type === 'payout_approved' || type === 'payout_completed') {
      window.location.href = '/earnings';
    } else {
      // Default to notifications page
      window.location.href = '/notifications';
    }
  }

  getToken() {
    return this.fcmToken;
  }

  isInitialized() {
    return this.isRegistered;
  }

  // Method to remove all listeners (cleanup)
  async removeAllListeners() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    
    const PushNotifications = await getPushNotifications();
    if (PushNotifications) {
      PushNotifications.removeAllListeners();
    }
  }
}

export default new PushNotificationService();

